import { ArrowPathIcon, CheckIcon } from '@heroicons/react/24/outline';
import React, { createRef, useEffect, useState } from 'react';
import type { ChangeEvent, FC } from 'react';

import { useCreateFontFile } from '@/api/queries/font-file';
import type { Font } from '@/api/types/font';
import { UploadIcon } from '@/common/upload-icon';
import { type SessionId, createSession, destroySession, loadFont } from '@/etcher';
import { getFontHash } from '@/utils';

import { Button, ButtonSize, ButtonType } from './button';
import { Modal } from './modal';

enum FontState {
    DEFAULT = 0,
    SUCCESS = 1,
    INVALID_FILE = 2,
    NO_MATCHING_FONT = 3,
}

interface MissingFontsModalProps {
    onUploadDone?: (font: Font) => void;
    isOpen: boolean;
    fonts: Font[];
    uploadedFonts: Font[];
    setIsOpen: (isOpen: boolean) => void;
}

export const MissingFontsModal: FC<MissingFontsModalProps> = ({
    onUploadDone,
    isOpen,
    setIsOpen,
    fonts,
    uploadedFonts,
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fontUiStates, setFontStates] = useState<Record<string, FontState>>({});
    const { mutate: createFontFile } = useCreateFontFile();

    useEffect(() => {
        setFontStates((prevFontStates) => {
            const newFontStates: Record<string, FontState> = {};
            for (const font of fonts) {
                const fontHash = getFontHash(font);
                const isUploaded = uploadedFonts.some((uploadedFont) => fontHash === getFontHash(uploadedFont));
                if (isUploaded) {
                    newFontStates[fontHash] = FontState.SUCCESS;
                } else {
                    newFontStates[fontHash] = prevFontStates[fontHash] || FontState.DEFAULT;
                }
            }
            return newFontStates;
        });
    }, [fonts, uploadedFonts]);

    const updateFontState = (font: Font, state: FontState) => {
        setFontStates((prevFontStates) => {
            const fontHash = getFontHash(font);
            return {
                ...prevFontStates,
                [fontHash]: state,
            };
        });
    };

    const handleFileChange = (font: Font, event: ChangeEvent<HTMLInputElement>) => {
        const file = event?.target?.files?.[0];

        if (file) {
            setIsLoading(true);
            file.arrayBuffer().then((arrBuf) => {
                createSession((res) => {
                    const etcherSession = res.sessionId;

                    loadFont(etcherSession, arrBuf, (newFonts) => {
                        if (newFonts.length === 0) {
                            updateFontState(font, FontState.INVALID_FILE);
                            setIsLoading(false);
                            return;
                        }

                        const fontHash = getFontHash(font);
                        const hasMatchingFont = newFonts.some((newFont) => fontHash === getFontHash(newFont));
                        if (!hasMatchingFont) {
                            updateFontState(font, FontState.NO_MATCHING_FONT);
                            setIsLoading(false);
                            return;
                        }

                        createFontFile(file, {
                            onSuccess: () => {
                                setIsLoading(false);
                                for (const f of newFonts) {
                                    onUploadDone?.(f);
                                }
                            }
                        });

                        destroySession(etcherSession);
                    });
                });
            });
        }
    };

    return (
        <Modal isOpen={isOpen}>
            <div className="flex flex-col absolute min-w-64 w-[30vw] max-w-[650px] bg-gray-150 shadow-xl rounded-xl transition-all duration-300 ease-in-out">
                <div className="flex flex-col justify-between p-4 items-center">
                    <p className="text-xl text-white">
                        The template uses fonts that are currently not available in your organization.
                    </p>
                    <p className="text-sm text-white mt-1">
                        To ensure the design remains intact, upload the missing fonts first.
                    </p>
                </div>
                <hr className="bg-gray-100 border-gray-120" />
                <div className="flex flex-col p-4 gap-2">
                    <p className="text-sm text-gray-100">Missing Fonts ({fonts.length})</p>
                    <div className="flex flex-col max-h-[25vh] overflow-y-auto no-scrollbar bg-gray-140 rounded-md">
                        {fonts.map((font) => {
                            const fileInputRef = createRef<HTMLInputElement>();
                            const fontHash = getFontHash(font);
                            const uiState = fontUiStates[fontHash];

                            return (
                                <div key={getFontHash(font)} className="flex flex-col w-full px-3 py-2">
                                    <p className="text-sm text-white">
                                        {[font.family, font.style, font.weight].join(' ')}
                                    </p>
                                    {uiState === FontState.DEFAULT && (
                                        <button
                                            type="button"
                                            disabled={isLoading}
                                            className="flex flex-row items-center gap-1.5 w-full py-1 text-blue-30 text-xs"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <UploadIcon className="h-4 w-4 stroke-blue-30" />
                                            Upload Font
                                        </button>
                                    )}
                                    {uiState === FontState.SUCCESS && (
                                        <div className="flex flex-row items-center gap-1.5 w-full py-1 text-green-60 text-xs">
                                            <CheckIcon className="h-4 w-4 stroke-green-60" />
                                            Font Ready
                                        </div>
                                    )}
                                    {uiState === FontState.INVALID_FILE && (
                                        <div className="flex flex-row items-center gap-1.5 w-full py-1 text-xs">
                                            <span className=" text-red-50">Invalid Font File</span>
                                            <button
                                                type="button"
                                                className="flex flex-row items-center gap-1.5 text-blue-30"
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                <ArrowPathIcon className="h-4 w-4 stroke-blue-30" />
                                                Retry
                                            </button>
                                        </div>
                                    )}
                                    {uiState === FontState.NO_MATCHING_FONT && (
                                        <div className="flex flex-row items-center gap-1.5 w-full py-1 text-xs">
                                            <span className=" text-red-50">No Matching Font</span>
                                            <button
                                                type="button"
                                                className="flex flex-row items-center gap-1.5 text-blue-30"
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                <ArrowPathIcon className="h-4 w-4 stroke-blue-30" />
                                                Retry
                                            </button>
                                        </div>
                                    )}
                                    <input
                                        id={`${font.family}-${font.style}-${font.weight}`}
                                        type="file"
                                        accept="font/*"
                                        ref={fileInputRef}
                                        onChange={(e) => {
                                            handleFileChange(font, e);
                                        }}
                                        multiple={false}
                                        style={{ display: 'none' }}
                                    />
                                    <hr className="bg-gray-100 border-gray-120 opacity-20 mt-1" />
                                </div>
                            );
                        })}
                    </div>
                    <hr className="mt-2 bg-gray-100 border-gray-120" />
                    <div className="w-full flex flex-row justify-end gap-3 p-6">
                        <Button
                            title="Cancel"
                            className="hover:bg-gray-160"
                            buttonType={ButtonType.SECONDARY}
                            buttonSize={ButtonSize.LARGE}
                            onClick={() => {
                                setIsOpen(false);
                            }}
                        />
                        <Button
                            title="Continue"
                            buttonType={ButtonType.PRIMARY}
                            buttonSize={ButtonSize.LARGE}
                            disabled={uploadedFonts.length !== fonts.length}
                            onClick={() => {
                                setIsOpen(false);
                            }}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};
